<template>
  <div :style="previewWrapperStyle" v-if="props.preview">
    <iframe :style="previewStyle" :srcdoc="renderHTML" ref="previewFrame" scrolling="no" />
    <button
      v-if="allowFullscreen"
      class="btn btn-sm btn-ghost-primary btn-icon"
      v-tooltip="'Fullscreen'"
      @click="openFull = true">
      <IconArrowsMaximize :size="14" />
    </button>
    <div class="preview-overlay" @click="openFull = true" />
    <Transition>
      <div
        class="fullscreen-preview"
        v-if="openFull"
        @click.stop="openFull = false"
        @wheel.prevent
        @touchmove.prevent
        @scroll.prevent>
        <iframe
          :style="previewFullStyle"
          :srcdoc="renderHTMLFull"
          ref="previewFrameFull"
          :allowfullscreen="true"
          scrolling="no" />
        <IconX :size="20" role="button" class="close-button" />
      </div>
    </Transition>
  </div>
</template>
<script setup lang="ts">
import type { OrderPreview } from '@/types/types';
import type { CSSProperties } from 'vue';
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';
import { request } from '@/api/client';
import { debounce } from '@prospective/lithium';
import { IconArrowsMaximize, IconX } from '@tabler/icons-vue';

const props = withDefaults(
  defineProps<{ preview: OrderPreview; allowFullscreen?: boolean; size?: number; padding?: number }>(),
  {
    allowFullscreen: true,
    size: 300,
    padding: 3,
  }
);

const previewWrapperStyle: CSSProperties = {
  width: '100%',
  maxWidth: `${props.size + props.padding * 2}px`,
  height: `${props.size + props.padding * 2}px`,
  backgroundColor: 'rgb(241, 245, 249)',
  padding: `${props.padding}px`,
  position: 'relative',
  boxSizing: 'border-box',
};

const renderHTML = ref<string>();
const renderHTMLFull = ref<string>();
const previewFrame = ref<HTMLIFrameElement>();
const previewFrameFull = ref<HTMLIFrameElement>();
const openFull = ref(false);
const fullSize = ref(Math.min(window.innerWidth, window.innerHeight));
// real content height reported by each iframe (e.g. after a "more" click grows it),
// overriding the preview's static height so the scale-to-fit math accounts for it
const reportedHeight = ref<number | null>(null);
const reportedFullHeight = ref<number | null>(null);

onMounted(() => {
  request('GET', `/v1/account/campaigns/${props.preview.campaignId}/preview/${props.preview.key}`, {
    video_muted: 1,
  }).then((res) => (renderHTML.value = res));
  window.addEventListener('resize', debounce(getFullSize, 600));
  window.addEventListener('message', onPreviewMessage);
});

onUnmounted(() => {
  window.removeEventListener('resize', debounce(getFullSize, 600));
  window.removeEventListener('message', onPreviewMessage);
});

function onPreviewMessage(event: MessageEvent) {
  if (event.data?.type !== 'pjas-preview-resize') return;

  if (event.source === previewFrame.value?.contentWindow) {
    reportedHeight.value = event.data.height;
  } else if (event.source === previewFrameFull.value?.contentWindow) {
    reportedFullHeight.value = event.data.height;
  }
}

const scale = computed(() => {
  if (props.preview) {
    const height = reportedHeight.value ?? props.preview.height;
    const scaleX = props.size / props.preview.width;
    const scaleY = props.size / height;
    return Math.min(scaleX, scaleY, 1);
  }
  return 1;
});

const fullScale = computed(() => {
  const height = reportedFullHeight.value ?? props.preview.height;
  const scaleX = fullSize.value / props.preview.width;
  const scaleY = fullSize.value / height;
  return Math.min(scaleX, scaleY, 0.95);
});

const previewStyle = computed<CSSProperties>(() => {
  const { width } = props.preview;
  const height = reportedHeight.value ?? props.preview.height;
  return {
    backgroundColor: 'white',
    transform: `scale(${scale.value})`,
    transformOrigin: '0 0',
    width: `${width}px`,
    height: `${height}px`,
    marginTop: `${(props.size - height * scale.value) / 2}px`,
    marginLeft: `${(props.size - width * scale.value) / 2}px`,
  };
});

const previewFullStyle = computed(() => {
  const { width } = props.preview;
  const height = reportedFullHeight.value ?? props.preview.height;
  return {
    backgroundColor: 'white',
    transform: `scale(${fullScale.value})`,
    transformOrigin: '0 0',
    width: `${width}px`,
    height: `${height}px`,
  };
});
const getFullSize = () => {
  fullSize.value = Math.min(window.innerWidth, window.innerHeight);
};

watch(openFull, (val) => {
  if (val) {
    // discard the previous session's reported height so a stale "more"-expanded size doesn't
    // carry over until the freshly reopened iframe reports its own
    reportedFullHeight.value = null;
    request('GET', `/v1/account/campaigns/${props.preview.campaignId}/preview/${props.preview.key}`).then(
      (res) => (renderHTMLFull.value = res)
    );
  }

  // Stop background scroll
  document.body.style.overflow = val ? 'hidden' : 'auto';
});
</script>
<style scoped>
.close-button {
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: pointer;
}

.preview-overlay {
  background-color: transparent;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  z-index: 1;
}

.fullscreen-preview {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1031;
  cursor: pointer;

  display: flex;
  justify-content: center;
  align-items: center;
}

.btn.btn-icon {
  position: absolute;
  right: 5px;
  top: 5px;
  z-index: 2;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
